import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Users, Calendar, FileSignature, Activity, FlaskConical, Pill } from "lucide-react";
import { apiFetch } from "@/lib/api-client";
import { z } from "zod";

export const Route = createFileRoute("/dashboard/")({
  component: Dashboard,
});

function StatTile({ label, icon: Icon, color, loading, value }: { label: string; icon: any; color: string; loading?: boolean; value?: number | string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
          <Icon className={`h-4 w-4 ${color}`} strokeWidth={1.8} />
        </div>
        <div className="mt-2 text-3xl font-bold tracking-tight">
          {loading || value === undefined ? <Skeleton className="h-7 w-16" /> : value}
        </div>
      </CardContent>
    </Card>
  );
}

function Dashboard() {
  const patientsQ = useQuery({
    queryKey: ["patients", "count"],
    queryFn: () => apiFetch("/api/patients?limit=1", { schema: z.object({ total: z.number() }) }),
  });
  const today = new Date().toISOString().slice(0, 10);
  const agendaQ = useQuery({
    queryKey: ["agenda", "today-count"],
    queryFn: () => apiFetch(`/api/agenda?from=${today}&to=${today}`, { schema: z.object({ items: z.array(z.any()) }) }),
  });
  const invoicesQ = useQuery({
    queryKey: ["invoices", "count"],
    queryFn: () => apiFetch("/api/billing/invoices", { schema: z.object({ items: z.array(z.any()) }) }),
  });

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Tableau de bord</h1>
        <p className="mt-1 text-sm text-muted-foreground">Vue d'ensemble temps réel — connecté à l'API unifiée</p>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        <StatTile label="Patients" icon={Users} color="text-blue-400" loading={patientsQ.isLoading} value={patientsQ.data?.total} />
        <StatTile label="RDV aujourd'hui" icon={Calendar} color="text-emerald-400" loading={agendaQ.isLoading} value={agendaQ.data?.items.length} />
        <StatTile label="Factures" icon={FileSignature} color="text-amber-400" loading={invoicesQ.isLoading} value={invoicesQ.data?.items.length} />
        <StatTile label="Labs" icon={FlaskConical} color="text-purple-400" value="OK" />
        <StatTile label="Pharma" icon={Pill} color="text-pink-400" value="OK" />
        <StatTile label="Activité" icon={Activity} color="text-cyan-400" value="LIVE" />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Bienvenue sur DIKDOC</CardTitle>
          <CardDescription>
            Plateforme unifiée — 1 base de code, 1 base de données, 1 UX/UI.
            Utilisez le menu ci-dessous pour accéder aux modules cliniques.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
            <Link to="/patients" className="rounded-lg border border-white/10 bg-white/[0.03] p-3 transition hover:bg-white/[0.06]">
              <Users className="h-4 w-4 text-blue-400" />
              <div className="mt-2 text-sm font-medium">Patients</div>
            </Link>
            <Link to="/consultation" className="rounded-lg border border-white/10 bg-white/[0.03] p-3 transition hover:bg-white/[0.06]">
              <Activity className="h-4 w-4 text-emerald-400" />
              <div className="mt-2 text-sm font-medium">Consultation</div>
            </Link>
            <Link to="/lab-results" className="rounded-lg border border-white/10 bg-white/[0.03] p-3 transition hover:bg-white/[0.06]">
              <FlaskConical className="h-4 w-4 text-purple-400" />
              <div className="mt-2 text-sm font-medium">Laboratoire</div>
            </Link>
            <Link to="/billing" className="rounded-lg border border-white/10 bg-white/[0.03] p-3 transition hover:bg-white/[0.06]">
              <FileSignature className="h-4 w-4 text-amber-400" />
              <div className="mt-2 text-sm font-medium">Facturation</div>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
